/**
 * ตัวช่วยของธีม "หนังสือเรียน" (D18) — ใช้เฉพาะหน้าเลือกหัวข้อและหน้าผลคะแนน
 *
 * ★ สีวิชามาจาก `color_key` ของวิชา (thai/math/sci/soc) ไม่ใช่จากชื่อไทย
 *   key ที่ไม่รู้จักหรือไม่มี → `.ck-brand` — หน้าต้องไม่พังเมื่อ key หายจากฐาน
 */
import { useEffect } from "react";

import type { TopicNode } from "./types";

const COLOR_KEYS = new Set([
  "thai",
  "math",
  "sci",
  "soc",
  "hist",
  "math1",
  "scien",
  "eng",
]);

export function colorClass(key: string | null | undefined): string {
  return key && COLOR_KEYS.has(key) ? `ck-${key}` : "ck-brand";
}

/** เปิด skin หนังสือเรียนตลอดอายุของหน้า — ถอดออกตอนออกจากหน้า หน้าทำข้อสอบจึงไม่โดน */
export function useBookSkin(): void {
  useEffect(() => {
    document.documentElement.dataset.skin = "book";
    return () => {
      delete document.documentElement.dataset.skin;
    };
  }, []);
}

export interface UnitTab {
  small: string;
  big: string;
  /** big เป็นตัวเลขล้วน (โชว์ตัวใหญ่) หรือข้อความ (โชว์เล็กลง) */
  numeric: boolean;
}

/**
 * ป้ายหน่วย: "บทที่ 3" → บทที่ / 3 · ไม่มี unit_label → คำเริ่มต้น / ลำดับที่ในแถบ
 * ป้ายที่ไม่มีช่องว่าง ("ทักษะ") → บรรทัดเดียว
 */
export function unitTab(
  label: string | null | undefined,
  ordinal: number,
  word: string,
): UnitTab {
  const text = (label ?? "").trim();
  if (!text) return { small: word, big: String(ordinal), numeric: true };
  const at = text.lastIndexOf(" ");
  if (at <= 0)
    return { small: "", big: text, numeric: /^[0-9๐-๙]+$/.test(text) };
  const big = text.slice(at + 1);
  return { small: text.slice(0, at), big, numeric: /^[0-9๐-๙]+$/.test(big) };
}

export interface Band {
  section: string;
  units: TopicNode[];
  questions: number;
  /**
   * D25 — หัวข้อแม่ที่แถบนี้สังกัด (ชั้นใต้หน่วย) · null = วิชาที่ไม่มีชั้นหัวข้อแม่
   *
   * ★ จำเป็นเพราะวิชาหนึ่งอาจมีหัวข้อแม่จากหนังสือคนละเล่ม และทั้งสองเล่มใช้ป้าย
   *   "เรื่องที่ 1-5" เหมือนกัน (เลขเรื่องเป็นของหนังสือ ห้ามเราไปเปลี่ยน — §8 ข้อ 3)
   *   ถ้าเรียงด้วยเลขเรื่องอย่างเดียว แถบของสองเล่มจะสลับกันจนอ่านไม่รู้เรื่อง
   */
  ownerId: string | null;
  ownerName: string | null;
}

function flatten(nodes: TopicNode[]): TopicNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children)]);
}

/**
 * จัดหัวข้อเป็นแถบ (section) → การ์ดหน่วย
 * - การ์ด = หัวข้อที่ไม่มีลูก หรือมีข้อผูกตรงของตัวเอง (หัวข้อแม่ที่เป็นแค่กล่องไม่ต้องเป็นการ์ด
 *   เพราะกดแล้วได้เท่ากับรวมลูกอยู่แล้ว — D17)
 * - section ของการ์ด = ของตัวเอง → ของบรรพบุรุษที่ใกล้ที่สุด → ชื่อวิชา
 * - ลำดับแถบตามที่พบครั้งแรก ลำดับการ์ดตามต้นไม้
 */
export function bandsOf(tree: TopicNode[], fallbackSection: string): Band[] {
  const all = flatten(tree);
  const byId = new Map(all.map((n) => [n.id, n]));
  const sectionOf = (n: TopicNode): string => {
    let cur: TopicNode | undefined = n;
    while (cur) {
      if (cur.section) return cur.section;
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return fallbackSection;
  };
  // เส้นทางจากรากถึงโหนด — ใช้หา "หัวข้อแม่" ซึ่งคือชั้นที่สอง (หน่วย → หัวข้อแม่ → กลุ่ม)
  const chainOf = (n: TopicNode): TopicNode[] => {
    const chain: TopicNode[] = [];
    let cur: TopicNode | undefined = n;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return chain;
  };
  const ownerOf = (n: TopicNode): TopicNode | null => {
    const chain = chainOf(n);
    return chain.length >= 2 ? chain[1]! : null;
  };
  const ownerOrder = new Map<string, number>();
  for (const n of all) {
    const owner = ownerOf(n);
    if (owner && !ownerOrder.has(owner.id))
      ownerOrder.set(owner.id, ownerOrder.size);
  }
  const bands: Band[] = [];
  for (const n of all) {
    const isCard =
      n.children.length === 0 || (n.direct_question_count ?? 0) > 0;
    if (!isCard) continue;
    const name = sectionOf(n);
    const owner = ownerOf(n);
    // แถบเดียวกันต้องอยู่ใต้หัวข้อแม่เดียวกันด้วย ไม่งั้นสองเล่มที่ตั้งชื่อแถบซ้ำกันจะถูกยุบรวม
    let band = bands.find(
      (b) => b.section === name && b.ownerId === (owner?.id ?? null),
    );
    if (!band) {
      band = {
        section: name,
        units: [],
        questions: 0,
        ownerId: owner?.id ?? null,
        ownerName: owner?.name ?? null,
      };
      bands.push(band);
    }
    band.units.push(n);
    band.questions += n.direct_question_count ?? n.question_count;
  }
  // ★ เรียงเหมือนสารบัญหนังสือ: แถบตามเลขหน่วย/บทที่น้อยสุดในแถบ (หน่วย 1 → 3 → 4) แถบที่ไม่มีเลข
  //   ไปท้าย · เท่ากันให้แถบใหญ่ก่อน · การ์ดในแถบเรียงตามเลขป้าย แล้วตามลำดับต้นไม้ (stable)
  const num = (n: TopicNode): number => {
    const m = /[0-9๐-๙]+/.exec(n.unit_label ?? "");
    if (!m) return Number.POSITIVE_INFINITY;
    return Number(
      m[0].replace(/[๐-๙]/g, (d) => String("๐๑๒๓๔๕๖๗๘๙".indexOf(d))),
    );
  };
  for (const band of bands) band.units.sort((x, y) => num(x) - num(y));
  // ★ เรียงด้วย (ลำดับหัวข้อแม่, เลขเรื่องน้อยสุดในแถบ) — หัวข้อแม่มาก่อนเสมอ
  //   แถบของเล่มเดียวกันจึงอยู่ติดกันเรียงตามสารบัญของเล่มนั้น
  const ownerRank = (b: Band): number =>
    b.ownerId === null
      ? Number.POSITIVE_INFINITY
      : (ownerOrder.get(b.ownerId) ?? Number.POSITIVE_INFINITY);
  bands.sort((x, y) => {
    const ox = ownerRank(x);
    const oy = ownerRank(y);
    if (ox !== oy) return ox - oy;
    const nx = Math.min(...x.units.map(num));
    const ny = Math.min(...y.units.map(num));
    if (nx !== ny) return nx - ny;
    return y.questions - x.questions;
  });
  return bands;
}

/** D21 — นาทีโดยประมาณของชุด n ข้อ: 45 วินาที/ข้อ ปัดขึ้นเป็นหลัก 5 (85 ข้อ → 63.75 → 65) */
export function estimateMinutes(n: number): number {
  return Math.ceil((n * 45) / 60 / 5) * 5;
}

/**
 * D22 — ป้ายการ์ดชุดรวม: นับป้ายบท/หน่วย/เรื่องที่ต่างกันของลูก โดยดึง **คำหน่วยนับจากคำนำหน้า** ของ unit_label
 * ("บทที่ 3" → บท · "หน่วยที่ 2" → หน่วย · "เรื่องที่ 1" → เรื่อง) · ลูกที่ไม่มีป้ายไม่นับ ·
 * ป้ายรูปแบบอื่น (เช่น "แบบฝึก" "หน้า 9–39") หรือคำนำหน้าไม่เหมือนกันทั้งชุด → null = แสดงแบบไม่นับ
 */
export function bundleUnits(
  children: ReadonlyArray<Pick<TopicNode, "unit_label">>,
): { n: number; word: string } | null {
  const labels = children
    .map((c) => (c.unit_label ?? "").trim())
    .filter(Boolean);
  if (!labels.length) return null;
  const words = new Set<string>();
  for (const label of labels) {
    const m = /^(\S+?)ที่\s*[0-9๐-๙]+$/.exec(label);
    if (!m) return null;
    words.add(m[1]!);
  }
  if (words.size !== 1) return null;
  return { n: new Set(labels).size, word: [...words][0]! };
}
